import "./site/error-beacon";
import { Bulwark, UpgradeKind, UpgradePurchase, UPGRADE_LABEL } from "./games/bulwark/bulwark";
import { recordRun, showXpToast } from "./site/profile";
import { getDailySeed, markDailyComplete, isDailyRun } from "./site/daily";
import "./ui/styles.css";

const PB_KEY = "aether.bulwark_pb";
const HANDLE_KEY = "aether.handle";

interface BulwarkRunStart { run_id: string; seed: number; token: string; game: string; }

const UPGRADE_COSTS_UI: Record<UpgradeKind, number[]> = {
  range: [8, 16, 24],
  burst: [12, 24],
  slow_field: [10, 20],
};
const UPGRADE_DESC: Record<UpgradeKind, string> = {
  range: "+ tap radius",
  burst: "blast every Nth kill",
  slow_field: "easier kills near core",
};

async function startBulwarkRun(): Promise<BulwarkRunStart | null> {
  try {
    const res = await fetch("/api/runs/bulwark", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!res.ok) return null;
    return (await res.json()) as BulwarkRunStart;
  } catch {
    return null;
  }
}

async function finishBulwarkRun(
  runId: string,
  handle: string,
  durationS: number,
  kills: { idx: number; t: number; hits_t?: number[] }[],
  upgrades: UpgradePurchase[],
  ultimates: { t: number }[],
) {
  try {
    const res = await fetch(`/api/runs/bulwark/${runId}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        handle, duration_s: durationS, kills, upgrades, ultimates,
        // v5(b): mode field. Bulwark has no challenge surface today, so
        // this collapses to 'ranked' for all submitted runs. Wired for
        // forward-compat — the column accepts 'challenge' when the
        // feature ships.
        mode: "ranked",
      }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const hud = document.querySelector(".hud") as HTMLElement;
const hudKills = document.getElementById("hud-kills")!;
const hudTime = document.getElementById("hud-time")!;
const hudLeaks = document.getElementById("hud-leaks")!;
const hudEnergy = document.getElementById("hud-energy");
const ultChip = document.getElementById("ult-chip") as HTMLElement | null;
const msgTitle = document.getElementById("msg-title")!;
const msgBody = document.getElementById("msg-body")!;
const btnStart = document.getElementById("btn-start") as HTMLButtonElement;
const submitForm = document.getElementById("submit-form") as HTMLFormElement;
const handleInputOverlay = document.getElementById("handle-input-overlay") as HTMLInputElement;
const handleError = document.getElementById("handle-error") as HTMLElement;
const btnSubmitScore = document.getElementById("btn-submit-score") as HTMLButtonElement;
const btnShare = document.getElementById("btn-share") as HTMLAnchorElement;
const shopOverlay = document.getElementById("shop-overlay") as HTMLElement | null;
const shopTimer = document.getElementById("shop-timer") as HTMLElement | null;
const shopButtons = document.querySelectorAll<HTMLButtonElement>(".shop-btn");
const waveRampFill = document.getElementById("wave-ramp-fill") as HTMLElement | null;
const waveRampLabel = document.getElementById("wave-ramp-label") as HTMLElement | null;

// Mirror the validator's spawn-density curve: 0.6/s at t=0 → 3.0/s at
// t=MAX_DURATION_S. Read-only signal — the curve is server-canonical.
const BULWARK_MAX_DURATION_S = 180;
const BULWARK_DENSITY_BASE = 0.6;
const BULWARK_DENSITY_PEAK = 3.0;

// v5(b): "where you died" badge per dossier §6.2. The game-over card
// echoes the categorical death cause + spawn-depth so the player feels
// the same shape the leaderboard stores. `early_exit` is silent — the
// player dismissed the run without dying, so there's nothing to label.
function formatDiedBy(diedBy: string | undefined, spawnIdx: number | undefined): string {
  if (!diedBy || diedBy === "early_exit") return "";
  if (spawnIdx === undefined) return "";
  if (diedBy === "leaks_exhausted") return `core breached at spawn ${spawnIdx}`;
  if (diedBy === "time_cap") return `held to spawn ${spawnIdx}`;
  return "";
}

// v5(b): build-stack summary per dossier §6.3 (upgrade-meta widget).
// Compact form so it can sit next to existing tags. Silent when the
// player bought nothing — the per-count "N upgrades" tag already
// covers that case in renderShare's preamble.
function formatUpgradeStacks(stacks: { range: number; burst: number; slow_field: number } | undefined): string {
  if (!stacks) return "";
  const parts: string[] = [];
  if (stacks.range > 0) parts.push(`R${stacks.range}`);
  if (stacks.burst > 0) parts.push(`B${stacks.burst}`);
  if (stacks.slow_field > 0) parts.push(`S${stacks.slow_field}`);
  if (parts.length === 0) return "";
  return `build ${parts.join("·")}`;
}

// Unicode sparkline ramp for per-archetype kill telemetry. Each archetype
// is sized relative to the run's largest archetype count so the bars
// stay readable across short and long runs. Zero-count archetypes are
// dropped (no noise from never-met categories).
const SPARK_GLYPHS = ["▁", "▂", "▃", "▄", "▅", "▆", "▇", "█"];
function sparkBar(count: number, peak: number): string {
  if (count <= 0 || peak <= 0) return SPARK_GLYPHS[0];
  const idx = Math.max(0, Math.min(SPARK_GLYPHS.length - 1, Math.round((count / peak) * (SPARK_GLYPHS.length - 1))));
  return SPARK_GLYPHS[idx];
}
function renderArchetypeSpark(tank: number, sprinter: number, splitter: number): string {
  const peak = Math.max(tank, sprinter, splitter);
  if (peak <= 0) return "";
  const parts: string[] = [];
  if (tank > 0) parts.push(`tank ${sparkBar(tank, peak)} ${tank}`);
  if (sprinter > 0) parts.push(`sprint ${sparkBar(sprinter, peak)} ${sprinter}`);
  if (splitter > 0) parts.push(`split ${sparkBar(splitter, peak)} ${splitter}`);
  if (parts.length === 0) return "";
  return ` · ${parts.join(" · ")}`;
}

try {
  const pb = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0;
  if (pb > 0) msgBody.textContent = `Best: ${pb.toLocaleString()} pts. Spend energy at the shop every 30s for upgrades.`;
} catch { /* private mode */ }

let game: Bulwark | null = null;
let activeRunId: string | null = null;
let activeSeed: number | null = null;
let pendingSubmit: {
  durationS: number;
  score: number;
  kills: number;
  leaks: number;
  killClaims: { idx: number; t: number; hits_t?: number[] }[];
  upgrades: UpgradePurchase[];
  ultimates: { t: number }[];
} | null = null;
let shopVisible = false;

shopButtons.forEach((btn) => {
  btn.addEventListener("click", () => {
    const kind = btn.dataset.kind as UpgradeKind | undefined;
    if (!kind || !game) return;
    const ok = game.purchaseUpgrade(kind);
    if (!ok) btn.classList.add("denied");
    else btn.classList.add("bought");
    window.setTimeout(() => {
      btn.classList.remove("denied");
      btn.classList.remove("bought");
    }, 300);
  });
});

function updateShop(state: {
  energy: number;
  stacks: Record<UpgradeKind, number>;
  shopOpen: boolean;
  shopTimeLeft: number;
}) {
  if (!shopOverlay) return;
  if (state.shopOpen && !shopVisible) {
    shopOverlay.classList.add("visible");
    shopVisible = true;
  } else if (!state.shopOpen && shopVisible) {
    shopOverlay.classList.remove("visible");
    shopVisible = false;
  }
  if (shopVisible && shopTimer) {
    shopTimer.textContent = `${state.shopTimeLeft.toFixed(1)}s`;
  }
  shopButtons.forEach((btn) => {
    const kind = btn.dataset.kind as UpgradeKind | undefined;
    if (!kind) return;
    const level = state.stacks[kind];
    const costs = UPGRADE_COSTS_UI[kind];
    const maxed = level >= costs.length;
    const cost = maxed ? 0 : costs[level];
    btn.classList.toggle("disabled", maxed || state.energy < cost);
    const costEl = btn.querySelector(".shop-cost");
    const levelEl = btn.querySelector(".shop-level");
    if (costEl) costEl.textContent = maxed ? "MAX" : `${cost} ⚡`;
    if (levelEl) levelEl.textContent = maxed ? `L${level}` : (level > 0 ? `L${level} → L${level + 1}` : "L1");
  });
}

async function startRun() {
  if (game) game.armAudio();
  if (game) game.dispose();

  const dseed = getDailySeed();
  const runStart = dseed === null ? await startBulwarkRun() : null;
  activeRunId = runStart?.run_id ?? null;
  const seed = dseed ?? runStart?.seed ?? Math.floor(Math.random() * 0x7fffffff);
  activeSeed = seed;

  game = new Bulwark(canvas, {
    onHud: (state) => {
      hudTime.textContent = state.durationS.toFixed(1);
      hudKills.textContent = state.kills.toLocaleString();
      hudLeaks.textContent = String(state.leaks);
      if (hudEnergy) hudEnergy.textContent = String(state.energy);
      if (waveRampFill) {
        const progress = Math.max(0, Math.min(1, state.durationS / BULWARK_MAX_DURATION_S));
        waveRampFill.style.width = (progress * 100).toFixed(1) + "%";
      }
      if (waveRampLabel) {
        const progress = Math.max(0, Math.min(1, state.durationS / BULWARK_MAX_DURATION_S));
        const density = BULWARK_DENSITY_BASE + progress * (BULWARK_DENSITY_PEAK - BULWARK_DENSITY_BASE);
        waveRampLabel.textContent = density.toFixed(1) + "/s";
      }
      if (ultChip) {
        if (state.ultimateReady) {
          ultChip.classList.add("ready");
          ultChip.textContent = "ULT READY · Q";
        } else {
          ultChip.classList.remove("ready");
          ultChip.textContent = `ULT ${state.ultimateCharge}/25`;
        }
      }
      updateShop(state);
    },
    onGameOver: async ({ durationS, score, kills, leaks, isNewBest, killClaims, upgrades, championKills, ultimates, tankKills, sprinterKills, splitterKills }) => {
      hud.dataset.state = "over";
      msgTitle.textContent = leaks >= 5 ? "Core breached" : "Hold complete";
      const newBestText = isNewBest ? " · new best" : "";
      const champText = championKills > 0 ? ` · ${championKills}★ champions` : "";
      const upText = upgrades.length > 0 ? ` · ${upgrades.length} upgrades` : "";
      const ultText = ultimates.length > 0 ? ` · ${ultimates.length}⚡ ult` : "";
      const archetypeSpark = renderArchetypeSpark(tankKills, sprinterKills, splitterKills);
      msgBody.textContent = `${kills} kills · ${leaks} leaks · ${durationS.toFixed(1)}s · ${score.toLocaleString()} pts${champText}${upText}${ultText}${newBestText}${archetypeSpark}`;
      btnStart.textContent = "Defend again";
      if (shopOverlay) { shopOverlay.classList.remove("visible"); shopVisible = false; }
      const xp = recordRun({ game: "bulwark", score });
      showXpToast(xp.earnedXp, xp.totalXp, xp.rank);
      if (isDailyRun()) markDailyComplete("bulwark", score);

      if (!activeRunId) return;
      const handle = localStorage.getItem(HANDLE_KEY) ?? "";
      if (handle) {
        const res = await finishBulwarkRun(activeRunId, handle, durationS, killClaims, upgrades, ultimates);
        if (res) renderShare(res, handle);
      } else {
        pendingSubmit = { durationS, score, kills, leaks, killClaims, upgrades, ultimates };
        submitForm.hidden = false;
        handleInputOverlay.value = "";
        const isCoarse = matchMedia("(pointer: coarse)").matches;
        if (!isCoarse) setTimeout(() => handleInputOverlay.focus({ preventScroll: true }), 80);
      }
    },
  }, seed);

  game.armAudio();
  try {
    const raw = localStorage.getItem("aether.settings");
    if (raw) {
      const s = JSON.parse(raw);
      if (typeof s.audioVolume === "number") game.setVolume(s.audioVolume);
      if (typeof s.muted === "boolean") game.setMuted(s.muted);
    }
  } catch { /* default */ }
  game.start();
  hud.dataset.state = "running";
  msgTitle.textContent = "Bulwark";
  msgBody.textContent = "";
}

btnStart.addEventListener("click", startRun);

const settingsVolume = document.getElementById("settings-volume") as HTMLInputElement | null;
const settingsMuted = document.getElementById("settings-muted") as HTMLInputElement | null;
function readSharedSettings(): { audioVolume?: number; muted?: boolean } {
  try { return JSON.parse(localStorage.getItem("aether.settings") || "{}"); }
  catch { return {}; }
}
function patchSharedSettings(p: Record<string, unknown>) {
  const s = { ...readSharedSettings(), ...p };
  try { localStorage.setItem("aether.settings", JSON.stringify(s)); } catch { /* private mode */ }
}
const initial = readSharedSettings();
if (settingsVolume) {
  settingsVolume.value = String(initial.audioVolume ?? 0.7);
  settingsVolume.addEventListener("input", () => {
    const v = parseFloat(settingsVolume.value);
    patchSharedSettings({ audioVolume: v });
    game?.setVolume(v);
  });
}
if (settingsMuted) {
  settingsMuted.checked = !!initial.muted;
  settingsMuted.addEventListener("change", () => {
    patchSharedSettings({ muted: settingsMuted.checked });
    game?.setMuted(settingsMuted.checked);
  });
}

function renderShare(
  res: {
    rank: number;
    total: number;
    score: number;
    duration_s?: number;
    kills?: number;
    leaks?: number;
    // v5(b) schema-v1 fields the game-over card uses.
    died_by?: string;
    died_at_spawn_idx?: number;
    upgrade_stacks?: { range: number; burst: number; slow_field: number };
  },
  handle: string,
) {
  msgBody.textContent += ` · ranked #${res.rank} of ${res.total}`;
  // v5(b): per dossier §6.2 "where you died" badge. Silent on early_exit.
  const diedLine = formatDiedBy(res.died_by, res.died_at_spawn_idx);
  if (diedLine) msgBody.textContent += ` · ${diedLine}`;
  // v5(b): per dossier §6.3 build-stack composition. Silent when no
  // upgrade landed (the preamble already shows the count).
  const buildLine = formatUpgradeStacks(res.upgrade_stacks);
  if (buildLine) msgBody.textContent += ` · ${buildLine}`;
  const params = new URLSearchParams({
    handle,
    score: String(res.score),
    rank: String(res.rank),
    game: "bulwark",
  });
  if (res.duration_s !== undefined) params.set("duration_s", res.duration_s.toFixed(2));
  if (res.kills !== undefined) params.set("bulwark_kills", String(res.kills));
  if (res.leaks !== undefined) params.set("bulwark_leaks", String(res.leaks));
  if (activeSeed !== null) params.set("seed", String(activeSeed));
  btnShare.href = `/share?${params.toString()}`;
  btnShare.hidden = false;
}

const HANDLE_RE = /^[A-Za-z0-9_-]{1,16}$/;

submitForm.addEventListener("submit", async (e) => {
  e.preventDefault();
  const value = handleInputOverlay.value.trim();
  if (!HANDLE_RE.test(value)) {
    handleError.hidden = false;
    return;
  }
  handleError.hidden = true;
  if (!pendingSubmit || !activeRunId) return;
  btnSubmitScore.disabled = true;
  btnSubmitScore.textContent = "Saving…";
  try { localStorage.setItem(HANDLE_KEY, value); } catch { /* private mode */ }
  const res = await finishBulwarkRun(activeRunId, value, pendingSubmit.durationS, pendingSubmit.killClaims, pendingSubmit.upgrades, pendingSubmit.ultimates);
  if (res) {
    renderShare(res, value);
    btnSubmitScore.textContent = "Saved";
  } else {
    btnSubmitScore.disabled = false;
    btnSubmitScore.textContent = "Save score";
    handleError.textContent = "Submission failed — try again.";
    handleError.hidden = false;
  }
  submitForm.hidden = true;
  pendingSubmit = null;
});

handleInputOverlay.addEventListener("input", () => {
  if (handleError.hidden) return;
  if (HANDLE_RE.test(handleInputOverlay.value.trim())) handleError.hidden = true;
});

window.addEventListener("keydown", (e) => {
  if (e.target instanceof HTMLInputElement) return;
  if (e.code === "Enter" && hud.dataset.state !== "running") {
    startRun();
  }
});

// expose UPGRADE_LABEL for any debug usage from console
(window as unknown as { UPGRADE_LABEL?: typeof UPGRADE_LABEL }).UPGRADE_LABEL = UPGRADE_LABEL;
